import { useFlag } from '@calendly/feature-check-react';
import { useBooleanState, useOnValueChange } from '@calendly/hooks';
import { extensionSafeRedirect, parseQuery } from '@calendly/http-tools';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Link } from '@calendly/ui/components/link';
import { Popup, Body, Footer } from '@calendly/ui/components/popup';
import { TextField } from '@calendly/ui/form/text-field';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useContext, useState, forwardRef } from 'react';
import { BlockModal } from 'components/meetings/block_modals';
import { isCalendlyMeeting } from 'shared/utils/meeting_helpers';
import { FullStoryMask } from '../../../../shared/fullstory_privacy';
import { MeetingDetailDependenciesContext } from '../meeting_detail_context';
const messageCss = "m1y0ns6m";
const Message = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(messageCss, props.className)} />;
});
const listCss = "l3xhi2q";
const List = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'ul';
  return <Component ref={ref} {...restProps} className={classNames(listCss, props.className)} />;
});
const CollectiveMessage = ({
  event
}) => {
  const message = event.pageName ? I18n.t('publisher.scheduled_events.cancel_event.collective.this_will_cancel_name', {
    page_name: event.pageName
  }) : I18n.t('publisher.scheduled_events.cancel_event.collective.this_will_cancel_count', {
    member_count: event.pageMembers.length
  });
  return <Message>
      <List>
        <li>{message}</li>
        <li>{I18n.t('publisher.scheduled_events.cancel_event.collective.cancellation_email_will_go_out')}</li>
      </List>
    </Message>;
};
const CHARACTER_LIMIT = 1000;
const CancelReason = ({
  errors,
  onFocus,
  onChange
}) => <TextField type="textarea" name="cancel_reason" placeholder={I18n.t('publisher.scheduled_events.cancel_reason_placeholder')} maxLength={CHARACTER_LIMIT} errors={errors} onFocus={onFocus} onChange={onChange} />;
const containerCss = "cb4ud70";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const inviteeCss = "ia5bp2b";
const Invitee = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(inviteeCss, props.className)} />;
});
const blockLinkCss = "b4a3jac";
const BlockLink = forwardRef((props, ref) => <Link ref={ref} {...props} as="button" type="button" className={classNames(blockLinkCss, props.className)} />);
const EventDetails = ({
  event,
  inviteeName
}) => <Container>
    <p>{event.name || event.eventType.name}</p>
    {inviteeName && <Invitee><FullStoryMask>{inviteeName}</FullStoryMask></Invitee>}
    {event.publisherStartTime}
    {' - '}
    {event.publisherEndTime}
  </Container>;
export const CancelMeetingModal = ({
  event,
  onClose,
  showReportModal,
  clearSelectedEvent,
  updateSelectedEvent
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('CancelMeetingModal must be wrapped in a MeetingDetailDependenciesProvider');
  }
  const {
    useCancelMeetingMutation
  } = dependencies;
  const [cancelReason, setCancelReason] = useState('');
  const {
    id,
    isCollective,
    eventType: {
      kind
    },
    hasCapturedCharges: isPaid
  } = event;
  const isGroup = kind === 'group';
  const inviteeName = isGroup ? null : event.invitee.name;
  const [cancelMeeting, {
    error,
    isLoading
  }] = useCancelMeetingMutation({
    fixedCacheKey: 'shared-cancel-meeting'
  });
  const [cancelled, setCancelled] = useState(false);
  const [errors, setErrors] = useState([]);
  const isPhone = useDeviceType('isPhone');
  const featureFlagEnabled = useFlag('trust_and_safety_reactive_block');
  const shouldShowReportButton = isCalendlyMeeting(event) && !featureFlagEnabled;
  const shouldShowBlockButton = featureFlagEnabled && !isGroup && isCalendlyMeeting(event);
  const [isBlockModalOpen, openBlockModal, closeBlockModal] = useBooleanState(false);
  const handleBlockSuccess = () => {
    updateSelectedEvent();
    onClose();
    if (isPhone) {
      clearSelectedEvent();
    }
  };
  const onDismiss = e => {
    onClose(e);
    const {
      return_to: returnToUrl,
      closeRedirectUrl
    } = parseQuery(window.location.search);
    extensionSafeRedirect(returnToUrl || closeRedirectUrl, true, {
      id,
      success: false
    });
  };
  const onReasonInputChange = e => {
    setCancelReason(e.target.value);
  };
  const onConfirm = async () => {
    const result = await cancelMeeting({
      eventId: id,
      reason: cancelReason
    });
    if (result.data.cancelled) {
      setCancelled(true);
    }
  };
  useOnValueChange(cancelled, () => {
    if (cancelled) {
      updateSelectedEvent();
      onClose();
      if (isPhone) {
        clearSelectedEvent();
      }
    }
  });
  useOnValueChange(error, () => {
    if (error?.type === 'validation') {
      setErrors(error.payload?.cancelReason);
    } else {
      setErrors([]);
    }
  });
  return <>
      <Popup title={I18n.t('publisher.action.cancel_event')} onClose={onClose}>
        <Body>
          <EventDetails event={event} inviteeName={inviteeName} />
          {isCollective ? <CollectiveMessage event={event} /> : <Message>{I18n.t('publisher.scheduled_events.cancel_event.solo')}</Message>}
          {isPaid && <Message>{I18n.t('publisher.dashboard.event.refund_notice')}</Message>}
          <CancelReason errors={errors} onChange={onReasonInputChange} />
          {shouldShowReportButton && <Button decoration="minimal" fontSize="medium" spacingTop={16} onClick={showReportModal}>
              {I18n.t('publisher.scheduled_events.event_details.report_this_event')}
            </Button>}
          {shouldShowBlockButton && <BlockLink onClick={openBlockModal}>
              {I18n.t('publisher.block_invitee.action.block_invitee')}
            </BlockLink>}
        </Body>
        <Footer>
          <Button decoration="secondary-outline" onClick={onDismiss}>
            {I18n.t('publisher.scheduled_events.cancel_event.cancel')}
          </Button>
          <Button loading={isLoading} onClick={onConfirm}>
            {I18n.t('publisher.scheduled_events.cancel_event.confirm')}
          </Button>
        </Footer>
      </Popup>
      {isBlockModalOpen && <BlockModal event={event} invitee={event.invitee} onClose={closeBlockModal} onBlockSuccess={handleBlockSuccess} />}
    </>;
};

require("./cancel_meeting_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./cancel_meeting_modal.js");